import { useMemo } from 'react';
import { trpc } from '@/lib/api/trpc';
import type { FunnelNode } from '../types';

/**
 * Builds a lookup key for instance screenshots.
 * Uses "instanceId:pagePath" to disambiguate multi-page deployments
 * (e.g. same instance serves /checkout and /thankyou with different screenshots).
 * Falls back to just instanceId when no pagePath is provided.
 */
export function screenshotKey(instanceId: string, pagePath?: string): string {
  return pagePath ? `${instanceId}:${pagePath.replace(/^\//, '')}` : instanceId;
}

/** The path a step's screenshot was captured under. */
function stepPagePath(config: FunnelNode['config'] | undefined): string | undefined {
  return config?.metadata?.internalPath ?? config?.pagePath ?? config?.path;
}

/**
 * Resolve the preview thumbnail for a step, in the same precedence the canvas
 * step boxes use: the capture job's own screenshot first, then the deployment
 * screenshot keyed by instance + page path, then the bare instance.
 *
 * Split steps resolve against their first variant — a direct link points at the
 * step, and which variant a visitor sees is decided at runtime by the split.
 *
 * NOTE: `FlowStepNode` and `FunnelFlowCanvas` still carry their own inlined
 * copies of this precedence (they also handle per-variant selection and the
 * desktop/mobile toggle). They should collapse onto this helper, but that
 * touches the canvas render path so it is deliberately left out of TAG-2126.
 */
export function resolveStepThumbnailUrl(
  node: FunnelNode,
  instanceScreenshots: Record<string, string> | undefined,
): string | undefined {
  const variant = node.variants?.[0];
  if (variant) {
    const variantShot = (node.metadata as any)?.variantScreenshots?.[variant.id];
    if (variantShot?.url) return variantShot.url;
    const vId = variant.config?.instanceId;
    if (vId) {
      return instanceScreenshots?.[screenshotKey(vId, stepPagePath(variant.config))]
        ?? instanceScreenshots?.[vId];
    }
    return undefined;
  }

  const previewScreenshot = (node.metadata as any)?.previewScreenshot;
  if (previewScreenshot?.url) return previewScreenshot.url;

  const instanceId = node.config?.instanceId;
  if (!instanceId) return undefined;
  return instanceScreenshots?.[screenshotKey(instanceId, stepPagePath(node.config))]
    ?? instanceScreenshots?.[instanceId];
}

/**
 * Fetches instance screenshots for the whole store in ONE slim query.
 * Returns a map of "instanceId:pagePath" -> screenshot URL for use as fallback when
 * step.metadata.previewScreenshot is not yet filled (e.g. before background capture).
 *
 * Previously fired getAvailableInstancesForStepType once per step type in the
 * funnel (≤7 heavy catalog queries whose versions/configs/editor payloads were
 * never read), each force-refetched on every design save (audit FE-14).
 */
export function useInstanceScreenshotsForFunnel(
  storeId: string,
  nodes: FunnelNode[] | undefined
): Record<string, string> {
  const { data } = trpc.plugins.getInstanceScreenshotsForStore.useQuery(
    { storeId },
    { enabled: !!storeId && !!nodes?.length }
  );

  return useMemo(() => {
    const map: Record<string, string> = {};
    for (const entry of data?.screenshots ?? []) {
      const url = entry.screenshots?.desktop || entry.screenshots?.mobile;
      if (!url) continue;
      map[screenshotKey(entry.instanceId, entry.path)] = url;
      // Also store under bare instanceId as fallback (last write wins, but better than nothing)
      if (!map[entry.instanceId]) {
        map[entry.instanceId] = url;
      }
    }
    return map;
  }, [data]);
}
